import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getVisitorStats, clearVisitorLogs } from "../utils/visitorTracker";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conversionCount, setConversionCount] = useState(0);

  const fetchStats = async () => {
    setLoading(true);
    const data = await getVisitorStats();
    setStats(data);

    // Calculate conversions count from localStorage history
    const history = JSON.parse(localStorage.getItem("converter_history") || "[]");
    setConversionCount(history.length);

    setLoading(false);
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleClearLogs = () => {
    if (
      window.confirm(
        "Ma laguba ahay in la tiro dhamaan xogta dadka soo booqday ee halkan ku kaydsan?"
      )
    ) {
      clearVisitorLogs();
      fetchStats();
    }
  };

  if (loading) {
    return (
      <div style={styles.loaderContainer}>
        <div style={styles.spinner}></div>
        <p style={{ marginTop: 16, color: "#64748b", fontWeight: 600 }}>
          Loading visitor analytics...
        </p>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      {/* ===== HEADER ===== */}
      <header style={styles.navbar}>
        <div style={styles.navTitleBox}>
          <div style={styles.logoBadge}>📊</div>
          <div>
            <h1 style={styles.navTitle}>Visitor Analytics Dashboard</h1>
            <p style={styles.navSubtitle}>
              Database-Free Visitor & Device Usage Tracker
            </p>
          </div>
        </div>

        <div style={styles.navActions}>
          <button style={styles.refreshBtn} onClick={fetchStats}>
            🔄 Refresh
          </button>
          <Link to="/" style={styles.backBtn}>
            ← Back to Converter
          </Link>
        </div>
      </header>

      {/* ===== METRIC STAT CARDS ===== */}
      <section style={styles.statsGrid}>
        {/* Card 1: Total Page Views */}
        <div style={styles.statCard}>
          <div style={styles.statTop}>
            <span style={styles.statLabel}>Total Page Views</span>
            <div style={styles.iconCircle}>👁️</div>
          </div>
          <h2 style={styles.statNumber}>{stats.totalPageViews}</h2>
          <span style={styles.pillBadge}>Public Global Count</span>
        </div>

        {/* Card 2: Unique Visitors */}
        <div style={styles.statCard}>
          <div style={styles.statTop}>
            <span style={styles.statLabel}>Unique Visitors</span>
            <div style={{ ...styles.iconCircle, background: "rgba(16, 185, 129, 0.1)", color: "#10b981" }}>
              👤
            </div>
          </div>
          <h2 style={styles.statNumber}>{stats.uniqueVisitors}</h2>
          <span style={{ ...styles.pillBadge, background: "#d1fae5", color: "#047857" }}>
            Active Session Tokens
          </span>
        </div>

        {/* Card 3: Recent Activity Sessions */}
        <div style={styles.statCard}>
          <div style={styles.statTop}>
            <span style={styles.statLabel}>Recorded Sessions</span>
            <div style={{ ...styles.iconCircle, background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>
              📱
            </div>
          </div>
          <h2 style={styles.statNumber}>{stats.logs.length}</h2>
          <span style={{ ...styles.pillBadge, background: "#f3e8ff", color: "#6b21a8" }}>
            Browser Local Logs
          </span>
        </div>

        {/* Card 4: Image Conversions */}
        <div style={styles.statCard}>
          <div style={styles.statTop}>
            <span style={styles.statLabel}>Local Conversions</span>
            <div style={{ ...styles.iconCircle, background: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" }}>
              🖼️
            </div>
          </div>
          <h2 style={styles.statNumber}>{conversionCount}</h2>
          <span style={{ ...styles.pillBadge, background: "#fef3c7", color: "#b45309" }}>
            Processed Files
          </span>
        </div>
      </section>

      {/* ===== CHARTS & BREAKDOWN SECTION ===== */}
      <section style={styles.twoColumnGrid}>
        {/* BROWSERS BREAKDOWN */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>🌐 Browsers</h3>
            <span style={styles.smallBadge}>Distribution</span>
          </div>

          {Object.keys(stats.browserCounts).length === 0 ? (
            <p style={styles.emptyText}>No browser data recorded yet</p>
          ) : (
            Object.entries(stats.browserCounts).map(([browser, count]) => {
              const percentage = Math.round((count / (stats.logs.length || 1)) * 100);
              return (
                <div key={browser} style={styles.barItem}>
                  <div style={styles.barMeta}>
                    <span style={styles.barName}>{browser}</span>
                    <span style={styles.barPercent}>{count} ({percentage}%)</span>
                  </div>
                  <div style={styles.barTrack}>
                    <div
                      style={{
                        ...styles.barFill,
                        width: `${percentage}%`,
                        background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* OPERATING SYSTEMS BREAKDOWN */}
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>💻 Operating Systems</h3>
            <span style={styles.smallBadge}>Platforms</span>
          </div>

          {Object.keys(stats.osCounts).length === 0 ? (
            <p style={styles.emptyText}>No OS data recorded yet</p>
          ) : (
            Object.entries(stats.osCounts).map(([os, count]) => {
              const percentage = Math.round((count / (stats.logs.length || 1)) * 100);
              return (
                <div key={os} style={styles.barItem}>
                  <div style={styles.barMeta}>
                    <span style={styles.barName}>{os}</span>
                    <span style={styles.barPercent}>{count} ({percentage}%)</span>
                  </div>
                  <div style={styles.barTrack}>
                    <div
                      style={{
                        ...styles.barFill,
                        width: `${percentage}%`,
                        background: "linear-gradient(90deg, #10b981, #059669)",
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* ===== RECENT VISITORS LOG TABLE ===== */}
      <section style={{ ...styles.card, marginTop: 24 }}>
        <div style={styles.tableHeaderSection}>
          <div>
            <h3 style={styles.cardTitle}>👥 Recent Visitor Sessions</h3>
            <p style={{ color: "#64748b", fontSize: 13, margin: "2px 0 0" }}>
              Client activity tracked safely without external databases
            </p>
          </div>
          <button style={styles.clearBtn} onClick={handleClearLogs}>
            🗑 Clear Visitor Logs
          </button>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thRow}>
                <th style={styles.th}>Visitor Token</th>
                <th style={styles.th}>Device & OS</th>
                <th style={styles.th}>Browser</th>
                <th style={styles.th}>Screen</th>
                <th style={styles.th}>Visits</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {stats.logs.map((log, i) => (
                <tr key={i} style={styles.tr}>
                  <td style={styles.td}>
                    <span style={styles.tokenText}>
                      {log.visitorId ? log.visitorId.substring(0, 14) + "..." : "Anonymous"}
                    </span>
                    {log.isNewVisitor && <span style={styles.newBadge}>NEW</span>}
                  </td>
                  <td style={styles.td}>
                    {log.device === "Mobile" ? "📱 Mobile" : "🖥 Desktop"} ({log.os})
                  </td>
                  <td style={styles.td}>
                    <span style={styles.browserTag}>{log.browser}</span>
                  </td>
                  <td style={{ ...styles.td, color: "#64748b" }}>{log.screenResolution}</td>
                  <td style={styles.td}>
                    <span style={styles.visitBadge}>{log.visitCount || 1}</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: "right", color: "#64748b" }}>
                    {new Date(log.timestamp).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                </tr>
              ))}

              {stats.logs.length === 0 && (
                <tr>
                  <td colSpan="6" style={styles.emptyTableTd}>
                    No visitor sessions recorded yet. Convert some images on the main page to generate visit logs!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={styles.footer}>
        <p>© 2026 Visitor Analytics • 100% Database-Free Engine</p>
      </footer>
    </div>
  );
}

/* ==========================================================================
   ADMIN PAGE STYLES
   ========================================================================== */

const styles = {
  pageContainer: {
    minHeight: "100vh",
    padding: "16px 20px 40px",
    maxWidth: 1100,
    margin: "0 auto",
  },
  navbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "18px 24px",
    background: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRadius: 20,
    border: "1px solid rgba(255, 255, 255, 0.9)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
    marginBottom: 28,
    flexWrap: "wrap",
    gap: 16,
  },
  navTitleBox: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  logoBadge: {
    width: 44,
    height: 44,
    borderRadius: 14,
    background: "linear-gradient(135deg, #0f172a, #334155)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 22,
    color: "#fff",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.2)",
  },
  navTitle: {
    fontSize: "clamp(18px, 4vw, 24px)",
    fontWeight: 800,
    color: "#0f172a",
    margin: 0,
  },
  navSubtitle: {
    fontSize: 13,
    color: "#64748b",
    margin: "2px 0 0",
  },
  navActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  refreshBtn: {
    padding: "10px 18px",
    background: "#f1f5f9",
    color: "#334155",
    border: "none",
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
  },
  backBtn: {
    padding: "10px 18px",
    background: "linear-gradient(135deg, #4f46e5, #6366f1)",
    color: "#fff",
    borderRadius: 12,
    textDecoration: "none",
    fontWeight: 700,
    fontSize: 14,
    boxShadow: "0 6px 20px rgba(79, 70, 229, 0.25)",
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginBottom: 24,
  },
  statCard: {
    background: "rgba(255, 255, 255, 0.88)",
    backdropFilter: "blur(16px)",
    padding: 20,
    borderRadius: 20,
    border: "1px solid rgba(255, 255, 255, 0.95)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
  },
  statTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#64748b",
  },
  iconCircle: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "rgba(79, 70, 229, 0.1)",
    color: "#4f46e5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
  },
  statNumber: {
    fontSize: 34,
    fontWeight: 800,
    color: "#0f172a",
    margin: "12px 0 8px",
  },
  pillBadge: {
    fontSize: 11,
    padding: "4px 10px",
    background: "#e0e7ff",
    color: "#4338ca",
    borderRadius: 999,
    fontWeight: 700,
  },
  twoColumnGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 20,
  },
  card: {
    background: "rgba(255, 255, 255, 0.92)",
    backdropFilter: "blur(20px)",
    padding: 24,
    borderRadius: 24,
    border: "1px solid rgba(255, 255, 255, 0.95)",
    boxShadow: "0 15px 40px rgba(0,0,0,0.05)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: "#0f172a",
    margin: 0,
  },
  smallBadge: {
    fontSize: 11,
    padding: "3px 8px",
    background: "#f1f5f9",
    color: "#64748b",
    borderRadius: 6,
    fontWeight: 700,
  },
  barItem: {
    marginBottom: 16,
  },
  barMeta: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 6,
    fontSize: 13,
  },
  barName: {
    fontWeight: 700,
    color: "#1e293b",
  },
  barPercent: {
    color: "#64748b",
    fontWeight: 600,
  },
  barTrack: {
    height: 10,
    background: "#f1f5f9",
    borderRadius: 999,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: 999,
    transition: "width 0.5s ease",
  },
  tableHeaderSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  clearBtn: {
    padding: "8px 14px",
    background: "#fee2e2",
    color: "#ef4444",
    border: "none",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  thRow: {
    background: "#f8fafc",
    borderRadius: 12,
  },
  th: {
    padding: "12px 14px",
    fontSize: 12,
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "1px solid #e2e8f0",
  },
  tr: {
    borderBottom: "1px solid #f1f5f9",
  },
  td: {
    padding: "14px",
    fontSize: 13,
    color: "#1e293b",
  },
  tokenText: {
    fontFamily: "monospace",
    fontWeight: 700,
    fontSize: 12,
  },
  newBadge: {
    marginLeft: 6,
    background: "#ef4444",
    color: "#fff",
    fontSize: 9,
    padding: "2px 6px",
    borderRadius: 4,
    fontWeight: 800,
  },
  browserTag: {
    padding: "3px 8px",
    background: "#f1f5f9",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  },
  visitBadge: {
    background: "#e0e7ff",
    color: "#4338ca",
    padding: "2px 8px",
    borderRadius: 999,
    fontWeight: 700,
    fontSize: 12,
  },
  emptyTableTd: {
    padding: 30,
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 14,
  },
  emptyText: {
    color: "#94a3b8",
    fontSize: 13,
  },
  loaderContainer: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    width: 44,
    height: 44,
    border: "4px solid #e2e8f0",
    borderTop: "4px solid #4f46e5",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  footer: {
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 13,
    paddingTop: 24,
  },
};
